import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  useNotificationDispatches,
  useNotificationInstantDispatches,
} from "services/hooks/rentals";
import { CHANNEL_OPTIONS, DISPATCH_STATUS_OPTIONS, EVENT_KEY_OPTIONS } from "../constants";

const LOG_TYPES = [
  { label: "Tenant (scheduled)", value: "TENANT" },
  { label: "Payout (scheduled)", value: "PAYOUT" },
  { label: "Instant (events)", value: "INSTANT" },
];

const DispatchLogsTab = () => {
  const [message, setMessage] = useState({ type: "", text: "" });
  const [logType, setLogType] = useState("TENANT");
  const [statusFilter, setStatusFilter] = useState("");
  const [channelFilter, setChannelFilter] = useState("");
  const [eventKeyFilter, setEventKeyFilter] = useState("");

  const {
    dispatches,
    payoutDispatches,
    isLoadingDispatches,
    dispatchesError,
    fetchDispatches,
    fetchPayoutDispatches,
  } = useNotificationDispatches({ status: statusFilter, channel: channelFilter });

  const {
    instantDispatches,
    isLoadingInstantDispatches,
    instantDispatchesError,
    fetchInstantDispatches,
  } = useNotificationInstantDispatches({
    status: statusFilter,
    channel: channelFilter,
    event_key: eventKeyFilter,
  });

  const isInstant = logType === "INSTANT";
  const logs = isInstant ? instantDispatches : logType === "TENANT" ? dispatches : payoutDispatches;
  const isLoading = isInstant ? isLoadingInstantDispatches : isLoadingDispatches;
  const loadError = isInstant ? instantDispatchesError : dispatchesError;

  const loadLogs = async () => {
    if (logType === "TENANT") {
      await fetchDispatches();
    } else if (logType === "PAYOUT") {
      await fetchPayoutDispatches();
    } else {
      await fetchInstantDispatches();
    }
  };

  useEffect(() => {
    loadLogs();
  }, [logType]);

  const eventLabel = (eventKey) => {
    const match = EVENT_KEY_OPTIONS.find((option) => option.value === eventKey);
    return match?.label || eventKey || "—";
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Dispatch Logs</Typography>
        <Button variant="outlined" onClick={loadLogs}>
          Refresh
        </Button>
      </Box>

      {message.text && (
        <Alert severity={message.type === "error" ? "error" : "success"} sx={{ mb: 2 }} onClose={() => setMessage({ type: "", text: "" })}>
          {message.text}
        </Alert>
      )}

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <FormControl fullWidth>
            <InputLabel>Log Type</InputLabel>
            <Select
              label="Log Type"
              value={logType}
              onChange={(event) => {
                setLogType(event.target.value);
                if (event.target.value !== "INSTANT") setEventKeyFilter("");
              }}
            >
              {LOG_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {isInstant && (
            <FormControl fullWidth>
              <InputLabel>Event</InputLabel>
              <Select
                label="Event"
                value={eventKeyFilter}
                onChange={(event) => setEventKeyFilter(event.target.value)}
              >
                <MenuItem value="">All events</MenuItem>
                {EVENT_KEY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <MenuItem value="">All</MenuItem>
              {DISPATCH_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Channel</InputLabel>
            <Select label="Channel" value={channelFilter} onChange={(event) => setChannelFilter(event.target.value)}>
              <MenuItem value="">All</MenuItem>
              {CHANNEL_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" onClick={loadLogs}>
            Apply
          </Button>
        </Stack>
      </Paper>

      <TableContainer component={Paper}>
        {isLoading ? (
          <Box p={4} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Channel</TableCell>
                {isInstant ? (
                  <>
                    <TableCell>Event</TableCell>
                    <TableCell>Template</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell>Rule</TableCell>
                    <TableCell>Template</TableCell>
                  </>
                )}
                <TableCell>Recipient</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isInstant ? 7 : 7}>No dispatch logs found.</TableCell>
                </TableRow>
              ) : (
                logs.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.status || "—"}</TableCell>
                    <TableCell>{row.channel || "—"}</TableCell>
                    {isInstant ? (
                      <>
                        <TableCell>{eventLabel(row.event_key)}</TableCell>
                        <TableCell>{row.related_template?.name || row.related_template?.id || "—"}</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>
                          {row.related_rule?.name ||
                            row.related_rule?.id ||
                            row.related_payout_rule?.name ||
                            row.related_payout_rule?.id ||
                            "—"}
                        </TableCell>
                        <TableCell>{row.related_template?.name || row.related_template?.id || "—"}</TableCell>
                      </>
                    )}
                    <TableCell>{row.recipient || row.recipient_email || row.recipient_phone || "—"}</TableCell>
                    <TableCell>{row.date_created || "—"}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
};

export default DispatchLogsTab;
